import React, { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, debounce, Divider, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { getPieChartOptions } from './chartWidgetUtility';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { ECElementEvent } from 'echarts';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Insights from 'components/DataEstate/Insights';
import FormattedNumber from './FormattedNumber';
import { ChangeIndicator } from './ChangeIndicator';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip, TooltipLabel } from './InfoTooltip';
import InsightsSection from 'components/Common/InsightsSection';

interface Insight {
	id?: number;
	title: string;
	description: string;
}

// Define the component props
interface WidgetProps {
    title?: any;
    value: string | number;
    change: number | string;
    changeType: 'increase' | 'decrease' | string;
    isLoading: boolean;
    isOverlayWidget?: boolean;
    tooltipLabel?: TooltipLabel;
    hasTooltip?: boolean;
    data?: any[];
    envNames?: string[];
    seriesData?: any;
    minTitleHeight?: `${number}px`;
    handleEvent?: (event: ECElementEvent) => void;
    radius: any,
    customLegendPosition?: boolean,
	customChartPosition?: any,
    showDollar?: boolean;
    shortDesc?: string | null;
    customSize?: string
    colorMapping?: any;
    enableSectorCut?: boolean;
    setEnableSectorCut?: any;
    handleNavigate?: () => void;
    loader?: boolean;
    fetchInsights: () => void;
    insights: Insights[];
    setInsights: React.Dispatch<React.SetStateAction<Insights[]>>;
    onAdd: (insight: Insight) => void;
    onUpdate: (insight: Insight) => void;
    onDelete: (id: number) => void;
    openDrawer?: any;
}

const defaultOptions = {
    graphic: [
        {
            type: 'circle',
            left: 'center',
            top: 'center',
            shape: {
                r: 100
            },
            style: {
                fill: '#71758f66'
            }
        }
    ],
    title: {
        show: true,
        textStyle: {
            color: '#bcbcbc',
            fontWeight: 'normal',
            fontSize: 16
        },
        text: 'No data',
        left: 'center',
        top: 'center'
    },
    backgroundColor: '#053367'
};

const PieInsightInfoWidget: React.FC<WidgetProps> = ({ title = '', value, change, changeType, isLoading = false,
    isOverlayWidget = false,
    tooltipLabel,
    hasTooltip = true,
    seriesData,
    radius,
    customLegendPosition = false,
	customChartPosition,
    minTitleHeight = '50.5px',
    handleEvent,
    customSize = '450px',
    showDollar = true,
    shortDesc = null,
    colorMapping,
    enableSectorCut,
    setEnableSectorCut,
    handleNavigate,
    loader,
	fetchInsights,
	insights,
	onAdd,
	onUpdate,
	onDelete,
    openDrawer,
}) => {
    const theme = useTheme();
    const colors = theme.palette;
    const chartContainerRef = useRef<HTMLDivElement>(null);
        const [windowSize, setWindowSize] = useState(window.innerWidth);
        const [gridLeft, setGridLeft] = useState('5%');
        const [expanded, setExpanded] = useState(false);

        const handleAccordionChange = async (_event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded);
            if (isExpanded) 
                fetchInsights(); 
        };
        useEffect(() => {
            document.body.classList.add('accord');
        }, [])
    
        useEffect(() => {
    
            // re-render chart to fix alignment when resizing the browser
            const handleResize = debounce(() => {
                if (chartContainerRef.current) {
                    const containerWidth = chartContainerRef.current.offsetWidth;

                    let calculatedLeft: string;

                    if (containerWidth < 420) {
                        calculatedLeft = '18%';
                    } else if (containerWidth < 600) {
                        calculatedLeft = '15%';
                    } else if (containerWidth < 820) {
                        calculatedLeft = '12%';
                    } else if (containerWidth < 920) {
                        calculatedLeft = '9%';
                    } else if (containerWidth < 1260) {
                        calculatedLeft = '7%';
                    } else {
                        calculatedLeft = '5%';
                    }

                    setGridLeft(calculatedLeft);
                    setWindowSize(window.innerWidth);
                }
            }, 200);
    
            handleResize();
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }, []);
    
        const result = getPieChartOptions(seriesData,chartContainerRef?.current?.offsetWidth, colorMapping ?? {}, showDollar, radius, customLegendPosition, customChartPosition);
        
        const adjustedOption: any = !seriesData.length
                ? defaultOptions
                : {
                        ...result,
                        grid: {
                            ...result,
                            left: gridLeft
                        }
                  };

    return (
        <Box
            sx={{
                backgroundColor: colors.widget.background.card,
                display: 'flex',
                flexDirection: 'column',
                color: colors.widget.text.main,
                padding: '16px',
                borderRadius: '5px',
            }}
        >
			<Box
				sx={{
					backgroundColor: colors.widget.background.card,
					display: 'flex',
					flexDirection: 'row',
					color: colors.widget.text.main,
					borderRadius: '5px',
					height: '170px'
				}}
			>
				
					<>
						{/* Left Section (55%) */}
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'flex-start',
								flex: '30%',
                                cursor: 'pointer'
							}}
                            onClick = {handleNavigate}
						>
                            <Stack direction="row" justifyContent="space-between" minHeight={minTitleHeight}>
                                <Typography
                                    variant='widgetTitle'
                                    sx={{ p: '0 !important', color: colors.widget.text.title }}
                                >
                                    <HighlightedText text={title} />
                                </Typography>
                                {hasTooltip && !!tooltipLabel ? <InfoTooltip label={tooltipLabel} /> : null}
                            </Stack>

                            <Stack flexGrow={1} justifyContent="center" sx={{ mt: 1 }}>
                                <Stack color={colors.widget.text.main} direction="column" alignItems="start">
                                    <Stack direction="row" alignItems="baseline" flexWrap="wrap">
                                        <FormattedNumber number={value} isCost={true} />
                                    </Stack>
                                </Stack>
                            </Stack>

							{/* Change Indicator */}
                            <ChangeIndicator
                                change={change}
                                changeType={changeType}
                                colors={colors}
                            />
                            {/* <Typography
                                sx={{
                                    color: '#009BDB',
                                    fontSize: {
                                        xs: '10px',
                                        sm: '16px',
                                        md: '18px',
                                        lg: '18px'
                                    },
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    mt: 1,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                }}
                                onClick={(e) => {openDrawer(e, title)}}
                                 >
                                View Software vs Hardware vs Labor Breakups
                                <ChevronRightIcon sx={{ color: '#009BD8' }}/>
                            </Typography> */}
						</Box>
                {!isLoading ? (
						<Stack flexGrow={1} justifyContent="center" sx={{flex: '55%', paddingLeft: '16px', paddingRight: '8px'}} key={windowSize} ref={chartContainerRef} minHeight={customSize}>
							{!!setEnableSectorCut && (
								<FormControlLabel
									style={{ justifyContent: 'end' }}
									control={
										<Switch
											color={!enableSectorCut ? 'warning' : 'info'}
											onChange={(e) => setEnableSectorCut(e.target.checked)}
											checked={enableSectorCut}
											size="small"
										/>
									}
									label={'Sector'}
								/>
							)}
							<ReactECharts
								style={{ width: '100%', height: '100%' }}
								option={adjustedOption}
								onEvents={{
									click: (params: ECElementEvent) => {
										if (handleEvent && seriesData.length) {
											handleEvent(params);
										}
									}
								}}
								opts={{ renderer: 'canvas', width: 'auto' }}
								notMerge={true}
								lazyUpdate={true}
							/>
						</Stack>
                        ) : (
                            <LoaderComponent />
                        )}
					</>
			</Box>
            <Divider
				orientation="horizontal"
				flexItem
				sx={{
					my: 1,
					borderColor: colors.textLight.dark,
				}}
			/>
			<Box
				sx={{
					backgroundColor: `${colors.widget.background.card} !important`,
				}}
			>
				<Accordion
					expanded={expanded}
					onChange={handleAccordionChange}
					sx={{
						background: `${colors.widget.background.card} !important`,
						backgroundColor: `${colors.widget.background.card} !important`,
						boxShadow: 'none',
						'&.Mui-expanded': {
							background: `${colors.widget.background.card} !important`,
							backgroundColor: `${colors.widget.background.card} !important`,
						},
						'&::before': {
							display: 'none',
						},
						'&.MuiAccordion-root .MuiAccordionDetails-root': {
							backgroundColor: `${colors.widget.background.card} !important`,
							background: `${colors.widget.background.card} !important`,
							border: 'none !important'
						},
					}}
				>
					<AccordionSummary
						sx={{
							background: `${colors.widget.background.card} !important`,
							backgroundColor: `${colors.widget.background.card} !important`,
							color: colors.textLight.dark,
							p: 0,
							'&.Mui-expanded': {
								background: `${colors.widget.background.card} !important`,
								backgroundColor: `${colors.widget.background.card} !important`,
							},
							'& .MuiAccordionSummary-content': {
								background: `${colors.widget.background.card} !important`,
								backgroundColor: `${colors.widget.background.card} !important`,
							},
							'& .MuiAccordionSummary-content.Mui-expanded': {
								background: `${colors.widget.background.card} !important`,
								backgroundColor: `${colors.widget.background.card} !important`,
							},
						}}
					>
						<Box display="flex" alignItems="center">
							<Typography
								variant='subHeader'
								sx={{ color: `${colors.textLight.dark} !important` }}
							>
								Insights/Actions
							</Typography>
							{expanded ? (
								<ExpandLess sx={{ color: `${colors.textLight.dark} !important`, ml: 1 }} />
							) : (
								<ExpandMore sx={{ color: `${colors.textLight.dark} !important`, ml: 1 }} />
							)}
						</Box>
					</AccordionSummary>

					{loader ? (
						<LoaderComponent bgColor={colors.widget.background.card} />
					) : (
						<AccordionDetails>
							<InsightsSection
								insights={insights}
								onAdd={onAdd}
								onUpdate={onUpdate}
								onDelete={onDelete}
							/>
						</AccordionDetails>
					)}
				</Accordion>
			</Box>
        </Box>
		);
};

export default PieInsightInfoWidget;